import { useCallback, useState } from "react";
import { useEditor, EditorContent, BubbleMenu } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Highlight from "@tiptap/extension-highlight";
import Link from "@tiptap/extension-link";
import CustomImage from "./CustomImage";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import TextStyle from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Placeholder from "@tiptap/extension-placeholder";
import CharacterCount from "@tiptap/extension-character-count";
import Strike from "@tiptap/extension-strike";
import {
    Bold,
    Italic,
    UnderlineIcon,
    Strikethrough,
    Heading1,
    Heading2,
    Heading3,
    List,
    ListOrdered,
    Quote,
    Minus,
    LinkIcon,
    AlignLeft,
    AlignCenter,
    AlignRight,
    AlignJustify,
    Highlighter,
    Palette,
    Undo,
    Redo,
    Type,
    FileText,
    Smile,
} from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from "../Dropdown";
import { Button } from "../Button";
import { Separator } from "../Seperator";
import { Popover, PopoverContent, PopoverTrigger } from "../Popover";
import { Input } from "../Input";
import CodeBlock from "@tiptap/extension-code-block";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../Tabs";
import { emojiCategories } from "../../../Admin/notifications/emojis";

const colors = [
    "#000000",
    "#434343",
    "#666666",
    "#999999",
    "#b7b7b7",
    "#cccccc",
    "#d9d9d9",
    "#efefef",
    "#f3f3f3",
    "#ffffff",
    "#980000",
    "#ff0000",
    "#ff9900",
    "#ffff00",
    "#00ff00",
    "#00ffff",
    "#4a86e8",
    "#0000ff",
    "#9900ff",
    "#ff00ff",
    "#e6b8af",
    "#f4cccc",
    "#fce5cd",
    "#fff2cc",
    "#d9ead3",
    "#d0e0e3",
    "#c9daf8",
    "#cfe2f3",
    "#d9d2e9",
    "#ead1dc",
    "#dd7e6b",
    "#ea9999",
    "#f9cb9c",
    "#ffe599",
    "#b6d7a8",
    "#a2c4c9",
    "#a4c2f4",
    "#9fc5e8",
    "#b4a7d6",
    "#d5a6bd",
    "#cc4125",
    "#e06666",
    "#f6b26b",
    "#ffd966",
    "#93c47d",
    "#76a5af",
    "#6d9eeb",
    "#6fa8dc",
    "#8e7cc3",
    "#c27ba0",
    "#a61c00",
    "#cc0000",
    "#e69138",
    "#f1c232",
    "#6aa84f",
    "#45818e",
    "#3c78d8",
    "#3d85c6",
    "#674ea7",
    "#a64d79",
    "#85200c",
    "#990000",
    "#b45f06",
    "#bf9000",
    "#38761d",
    "#134f5c",
    "#1155cc",
    "#0b5394",
    "#351c75",
    "#741b47",
    "#5b0f00",
    "#660000",
    "#783f04",
    "#7f6000",
    "#274e13",
    "#0c343d",
    "#1c4587",
    "#073763",
    "#20124d",
    "#4c1130",
];

const highlightColors = [
    "#ffeb3b",
    "#4caf50",
    "#2196f3",
    "#ff9800",
    "#f44336",
    "#9c27b0",
    "#607d8b",
    "#795548",
];

interface RichTextEditorProps {
    message: string;
    onMessageChange: (content: string) => void;
    limit: number;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
    message,
    onMessageChange,
    limit,
}) => {
    const [linkUrl, setLinkUrl] = useState("");
    const [isLinkPopoverOpen, setIsLinkPopoverOpen] = useState(false);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                bulletList: { keepMarks: true, keepAttributes: false },
                orderedList: { keepMarks: true, keepAttributes: false },
            }),
            Strike,
            CodeBlock,
            Underline,
            TextStyle,
            Color,
            Link.configure({
                openOnClick: false,
                HTMLAttributes: {
                    class: "text-blue-500 underline cursor-pointer",
                },
            }),
            Highlight.configure({ multicolor: true }),
            TextAlign.configure({ types: ["heading", "paragraph"] }),
            CustomImage.configure({
                HTMLAttributes: { class: "max-w-full h-auto rounded-lg" },
            }),
            Table.configure({ resizable: true }),
            TableRow,
            TableHeader,
            TableCell,
            TaskItem.configure({ nested: true }),
            TaskList,
            Placeholder.configure({
                placeholder: "Start writing your content here...",
            }),
            CharacterCount.configure({ limit: limit }),
        ],
        content: message,
        onUpdate: ({ editor }) => {
            onMessageChange(editor.getHTML());
        },
        editorProps: {
            attributes: {
                class: "prose prose-sm sm:prose lg:prose-lg xl:prose-2xl max-w-full focus:outline-none min-h-[400px] p-4 w-full marker:text-black",
            },
            handleDOMEvents: {
                beforeinput(view, event: InputEvent) {
                    const count = editor!.storage.characterCount.characters();
                    // block typing or pasting new text once at limit
                    if (
                        count >= limit &&
                        (event.inputType.startsWith("insert") ||
                            event.inputType === "insertFromPaste")
                    ) {
                        event.preventDefault();
                        return true;
                    }
                    return false;
                },
            },
        },
    });

    // const addImage = useCallback(() => {
    //     const url = window.prompt("Enter image URL:");
    //     if (url && editor) editor.chain().focus().setImage({ src: url }).run();
    // }, [editor]);

    // const addImageFromFile = useCallback(() => {
    //     const input = document.createElement("input");
    //     input.type = "file";
    //     input.accept = "image/*";
    //     input.onchange = (e) => {
    //         const file = (e.target as HTMLInputElement).files?.[0];
    //         if (file) {
    //             const reader = new FileReader();
    //             reader.onload = (ev) => {
    //                 const src = ev.target?.result as string;
    //                 if (src && editor)
    //                     editor.chain().focus().setImage({ src }).run();
    //             };
    //             reader.readAsDataURL(file);
    //         }
    //     };
    //     input.click();
    // }, [editor]);

    const setLink = useCallback(() => {
        if (linkUrl && editor) {
            editor
                .chain()
                .focus()
                .extendMarkRange("link")
                .setLink({ href: linkUrl })
                .run();
            setLinkUrl("");
            setIsLinkPopoverOpen(false);
        }
    }, [editor, linkUrl]);

    const unsetLink = useCallback(() => {
        if (editor) {
            editor.chain().focus().unsetLink().run();
            setIsLinkPopoverOpen(false);
        }
    }, [editor]);

    const addEmoji = (emoji: string) => {
        if (editor) editor.chain().focus().insertContent(emoji).run();
    };

    if (!editor) return null;

    return (
        <div className="w-full mx-auto border border-gray-200 rounded-lg shadow-sm">
            {/* Main Toolbar */}
            <div className="border-b border-gray-200 p-2 bg-gray-50 rounded-t-lg">
                <div className="flex flex-wrap items-center gap-1">
                    {/* Undo/Redo */}
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => editor.chain().focus().undo().run()}
                        disabled={!editor.can().undo()}
                        className="h-8 w-8 p-0"
                    >
                        <Undo className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => editor.chain().focus().redo().run()}
                        disabled={!editor.can().redo()}
                        className="h-8 w-8 p-0"
                    >
                        <Redo className="h-4 w-4" />
                    </Button>

                    <Separator orientation="vertical" className="h-6 mx-1" />

                    {/* Text Formatting */}
                    <Button
                        variant={editor.isActive("bold") ? "default" : "ghost"}
                        size="sm"
                        onClick={() =>
                            editor.chain().focus().toggleBold().run()
                        }
                        className="h-8 w-8 p-0"
                    >
                        <Bold className="h-4 w-4" />
                    </Button>
                    <Button
                        variant={
                            editor.isActive("italic") ? "default" : "ghost"
                        }
                        size="sm"
                        onClick={() =>
                            editor.chain().focus().toggleItalic().run()
                        }
                        className="h-8 w-8 p-0"
                    >
                        <Italic className="h-4 w-4" />
                    </Button>
                    <Button
                        variant={
                            editor.isActive("underline") ? "default" : "ghost"
                        }
                        size="sm"
                        onClick={() =>
                            editor.chain().focus().toggleUnderline().run()
                        }
                        className="h-8 w-8 p-0"
                    >
                        <UnderlineIcon className="h-4 w-4" />
                    </Button>
                    <Button
                        variant={
                            editor.isActive("strike") ? "default" : "ghost"
                        }
                        size="sm"
                        onClick={() =>
                            editor.chain().focus().toggleStrike().run()
                        }
                        className="h-8 w-8 p-0"
                    >
                        <Strikethrough className="h-4 w-4" />
                    </Button>

                    {/* Headings */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 px-2"
                            >
                                <Type className="h-4 w-4 mr-1" />
                                <span className="text-xs">
                                    {editor.isActive("heading", {
                                        level: 1,
                                    })
                                        ? "H1"
                                        : editor.isActive("heading", {
                                              level: 2,
                                          })
                                        ? "H2"
                                        : editor.isActive("heading", {
                                              level: 3,
                                          })
                                        ? "H3"
                                        : "P"}
                                </span>
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-white">
                            <DropdownMenuItem
                                onClick={() =>
                                    editor.chain().focus().setParagraph().run()
                                }
                                className={
                                    editor.isActive("paragraph")
                                        ? "bg-gray-100"
                                        : ""
                                }
                            >
                                <FileText className="h-4 w-4 mr-2" />
                                Paragraph
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={() =>
                                    editor
                                        .chain()
                                        .focus()
                                        .toggleHeading({ level: 1 })
                                        .run()
                                }
                                className={
                                    editor.isActive("heading", { level: 1 })
                                        ? "bg-gray-100"
                                        : ""
                                }
                            >
                                <Heading1 className="h-4 w-4 mr-2" />
                                Heading 1
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={() =>
                                    editor
                                        .chain()
                                        .focus()
                                        .toggleHeading({ level: 2 })
                                        .run()
                                }
                                className={
                                    editor.isActive("heading", { level: 2 })
                                        ? "bg-gray-100"
                                        : ""
                                }
                            >
                                <Heading2 className="h-4 w-4 mr-2" />
                                Heading 2
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={() =>
                                    editor
                                        .chain()
                                        .focus()
                                        .toggleHeading({ level: 3 })
                                        .run()
                                }
                                className={
                                    editor.isActive("heading", { level: 3 })
                                        ? "bg-gray-100"
                                        : ""
                                }
                            >
                                <Heading3 className="h-4 w-4 mr-2" />
                                Heading 3
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>

                    <Separator orientation="vertical" className="h-6 mx-1" />

                    {/* Text Color */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                            >
                                <Palette className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="w-64 bg-white">
                            <div className="p-2">
                                <div className="text-xs font-medium mb-2">
                                    Text Color
                                </div>
                                <div className="grid grid-cols-10 gap-1">
                                    {colors.map((color) => (
                                        <button
                                            key={color}
                                            className="w-6 h-6 rounded border border-gray-200 hover:scale-110 transition-transform"
                                            style={{
                                                backgroundColor: color,
                                            }}
                                            onClick={() =>
                                                editor
                                                    .chain()
                                                    .focus()
                                                    .setColor(color)
                                                    .run()
                                            }
                                        />
                                    ))}
                                </div>
                            </div>
                        </DropdownMenuContent>
                    </DropdownMenu>

                    {/* Highlight */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant={
                                    editor.isActive("highlight")
                                        ? "default"
                                        : "ghost"
                                }
                                size="sm"
                                className="h-8 w-8 p-0"
                            >
                                <Highlighter className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-white">
                            <div className="p-2">
                                <div className="text-xs font-medium mb-2">
                                    Highlight Color
                                </div>
                                <div className="grid grid-cols-4 gap-1">
                                    {highlightColors.map((color) => (
                                        <button
                                            key={color}
                                            className="w-6 h-6 rounded border border-gray-200 hover:scale-110 transition-transform"
                                            style={{
                                                backgroundColor: color,
                                            }}
                                            onClick={() =>
                                                editor
                                                    .chain()
                                                    .focus()
                                                    .toggleHighlight({
                                                        color,
                                                    })
                                                    .run()
                                            }
                                        />
                                    ))}
                                </div>
                                <DropdownMenuSeparator />
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                        editor
                                            .chain()
                                            .focus()
                                            .unsetHighlight()
                                            .run()
                                    }
                                    className="w-full"
                                >
                                    Remove Highlight
                                </Button>
                            </div>
                        </DropdownMenuContent>
                    </DropdownMenu>

                    <Separator orientation="vertical" className="h-6 mx-1" />

                    {/* Alignment */}
                    <Button
                        variant={
                            editor.isActive({ textAlign: "left" })
                                ? "default"
                                : "ghost"
                        }
                        size="sm"
                        onClick={() =>
                            editor.chain().focus().setTextAlign("left").run()
                        }
                        className="h-8 w-8 p-0"
                    >
                        <AlignLeft className="h-4 w-4" />
                    </Button>
                    <Button
                        variant={
                            editor.isActive({ textAlign: "center" })
                                ? "default"
                                : "ghost"
                        }
                        size="sm"
                        onClick={() =>
                            editor.chain().focus().setTextAlign("center").run()
                        }
                        className="h-8 w-8 p-0"
                    >
                        <AlignCenter className="h-4 w-4" />
                    </Button>
                    <Button
                        variant={
                            editor.isActive({ textAlign: "right" })
                                ? "default"
                                : "ghost"
                        }
                        size="sm"
                        onClick={() =>
                            editor.chain().focus().setTextAlign("right").run()
                        }
                        className="h-8 w-8 p-0"
                    >
                        <AlignRight className="h-4 w-4" />
                    </Button>
                    <Button
                        variant={
                            editor.isActive({ textAlign: "justify" })
                                ? "default"
                                : "ghost"
                        }
                        size="sm"
                        onClick={() =>
                            editor.chain().focus().setTextAlign("justify").run()
                        }
                        className="h-8 w-8 p-0"
                    >
                        <AlignJustify className="h-4 w-4" />
                    </Button>

                    <Separator orientation="vertical" className="h-6 mx-1" />

                    {/* Lists */}
                    <Button
                        variant={
                            editor.isActive("bulletList") ? "default" : "ghost"
                        }
                        size="sm"
                        onClick={() =>
                            editor.chain().focus().toggleBulletList().run()
                        }
                        className="h-8 w-8 p-0"
                    >
                        <List className="h-4 w-4" />
                    </Button>
                    <Button
                        variant={
                            editor.isActive("orderedList") ? "default" : "ghost"
                        }
                        size="sm"
                        onClick={() =>
                            editor.chain().focus().toggleOrderedList().run()
                        }
                        className="h-8 w-8 p-0"
                    >
                        <ListOrdered className="h-4 w-4" />
                    </Button>

                    <Separator orientation="vertical" className="h-6 mx-1" />

                    {/* Quote & Divider */}
                    <Button
                        variant={
                            editor.isActive("blockquote") ? "default" : "ghost"
                        }
                        size="sm"
                        onClick={() =>
                            editor.chain().focus().toggleBlockquote().run()
                        }
                        className="h-8 w-8 p-0"
                    >
                        <Quote className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                            editor.chain().focus().setHorizontalRule().run()
                        }
                        className="h-8 w-8 p-0"
                    >
                        <Minus className="h-4 w-4" />
                    </Button>

                    <Separator orientation="vertical" className="h-6 mx-1" />

                    {/* Link */}
                    <Popover
                        open={isLinkPopoverOpen}
                        onOpenChange={setIsLinkPopoverOpen}
                    >
                        <PopoverTrigger asChild>
                            <Button
                                variant={
                                    editor.isActive("link")
                                        ? "default"
                                        : "ghost"
                                }
                                size="sm"
                                className="h-8 w-8 p-0"
                            >
                                <LinkIcon className="h-4 w-4" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-80">
                            <div className="space-y-2">
                                <div className="text-sm font-medium">
                                    Add Link
                                </div>
                                <Input
                                    placeholder="Enter URL"
                                    value={linkUrl}
                                    onChange={(e) => setLinkUrl(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            setLink();
                                        }
                                    }}
                                />
                                <div className="flex gap-2">
                                    <Button
                                        size="sm"
                                        onClick={setLink}
                                        disabled={!linkUrl}
                                    >
                                        Add Link
                                    </Button>
                                    {editor.isActive("link") && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={unsetLink}
                                        >
                                            Remove Link
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </PopoverContent>
                    </Popover>

                    {/* Image */}
                    {/* <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                            >
                                <ImageIcon className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-white">
                            <DropdownMenuItem onClick={addImageFromFile}>
                                <Upload className="h-4 w-4 mr-2" />
                                Upload Image
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={addImage}>
                                <LinkIcon className="h-4 w-4 mr-2" />
                                Image from URL
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu> */}

                    {/* Emoji */}
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button
                                variant="ghost"
                                size="sm"
                                title="Insert Emoji"
                            >
                                <Smile className="w-4 h-4" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-80 p-0">
                            <Tabs defaultValue="smileys" className="w-full">
                                <TabsList className="grid w-full grid-cols-5">
                                    <TabsTrigger
                                        value="smileys"
                                        className="text-xs"
                                    >
                                        😀
                                    </TabsTrigger>
                                    <TabsTrigger
                                        value="animals"
                                        className="text-xs"
                                    >
                                        🐶
                                    </TabsTrigger>
                                    <TabsTrigger
                                        value="food"
                                        className="text-xs"
                                    >
                                        🍎
                                    </TabsTrigger>
                                    <TabsTrigger
                                        value="activities"
                                        className="text-xs"
                                    >
                                        ⚽
                                    </TabsTrigger>
                                    <TabsTrigger
                                        value="objects"
                                        className="text-xs"
                                    >
                                        📱
                                    </TabsTrigger>
                                </TabsList>
                                {Object.entries(emojiCategories).map(
                                    ([key, category]) => (
                                        <TabsContent
                                            key={key}
                                            value={key}
                                            className="p-4"
                                        >
                                            <div className="grid grid-cols-8 gap-2 max-h-48 overflow-y-auto">
                                                {category.emojis.map(
                                                    (emoji, index) => (
                                                        <button
                                                            key={index}
                                                            className="text-xl hover:bg-gray-100 rounded p-1 transition-colors"
                                                            onClick={() =>
                                                                addEmoji(emoji)
                                                            }
                                                            title={emoji}
                                                        >
                                                            {emoji}
                                                        </button>
                                                    )
                                                )}
                                            </div>
                                        </TabsContent>
                                    )
                                )}
                            </Tabs>
                        </PopoverContent>
                    </Popover>
                </div>
            </div>

            {/* Bubble Menu */}
            {editor && (
                <BubbleMenu
                    editor={editor}
                    tippyOptions={{ duration: 100 }}
                    className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg shadow-lg p-1"
                >
                    <Button
                        variant={editor.isActive("bold") ? "default" : "ghost"}
                        size="sm"
                        onClick={() =>
                            editor.chain().focus().toggleBold().run()
                        }
                        className="h-8 w-8 p-0"
                    >
                        <Bold className="h-4 w-4" />
                    </Button>
                    <Button
                        variant={
                            editor.isActive("italic") ? "default" : "ghost"
                        }
                        size="sm"
                        onClick={() =>
                            editor.chain().focus().toggleItalic().run()
                        }
                        className="h-8 w-8 p-0"
                    >
                        <Italic className="h-4 w-4" />
                    </Button>
                    <Button
                        variant={
                            editor.isActive("underline") ? "default" : "ghost"
                        }
                        size="sm"
                        onClick={() =>
                            editor.chain().focus().toggleUnderline().run()
                        }
                        className="h-8 w-8 p-0"
                    >
                        <UnderlineIcon className="h-4 w-4" />
                    </Button>
                    <Separator orientation="vertical" className="h-6 mx-1" />
                    <Button
                        variant={
                            editor.isActive("strike") ? "default" : "ghost"
                        }
                        size="sm"
                        onClick={() =>
                            editor.chain().focus().toggleStrike().run()
                        }
                        className="h-8 w-8 p-0"
                    >
                        <Strikethrough className="h-4 w-4" />
                    </Button>
                    <Separator orientation="vertical" className="h-6 mx-1" />
                    <Button
                        variant={editor.isActive("link") ? "default" : "ghost"}
                        size="sm"
                        onClick={() => setIsLinkPopoverOpen(true)}
                        className="h-8 w-8 p-0"
                    >
                        <LinkIcon className="h-4 w-4" />
                    </Button>
                </BubbleMenu>
            )}

            {/* Editor Content */}
            <div className="min-h-[400px] p-4 w-full mx-0 px-0">
                <EditorContent editor={editor} />
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 p-2 bg-gray-50 rounded-b-lg">
                <div className="flex justify-between items-center text-xs text-gray-500">
                    <div>
                        {editor.storage.characterCount.characters()} / {limit}
                        {editor.storage.characterCount.limit} characters
                    </div>
                    <div>{editor.storage.characterCount.words()} words</div>
                </div>
            </div>
        </div>
    );
};

export default RichTextEditor;
